import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, BookOpen, FileCheck, Info, CheckCircle } from 'lucide-react';
import { isDriveUrl, toEmbedUrl } from '@/utils/drive';

export default function CourseViewer() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [idx, setIdx] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const videoEndedRef = useRef({});

  useEffect(() => {
    (async () => {
      try {
        await api.startCourse(courseId).catch(() => {});
        const r = await api.getCourse(courseId);
        setCourse(r.data);
        const cur = r.data.user_progress?.current_slide || 0;
        setIdx(cur);
        setMaxReached(cur);
      } catch (e) { toast.error('Error al cargar curso'); }
    })();
  }, [courseId]);

  if (!course) return <DashboardLayout><div className="p-8">Cargando...</div></DashboardLayout>;

  const slides = course.slides || [];
  const total = slides.length;
  const slide = slides[idx];
  const allCompleted = maxReached >= total - 1;

  const handleNext = async () => {
    const next = Math.min(idx + 1, total - 1);
    setIdx(next);
    if (next > maxReached) setMaxReached(next);
    try { await api.updateCourseProgress(courseId, next); } catch (e) { /* */ }
  };

  const handlePrev = () => setIdx(Math.max(idx - 1, 0));

  const canAdvance = () => {
    if (!slide) return false;
    if (slide.slide_type !== 'video') return true;
    // Para videos en Drive/YouTube (iframe) no podemos detectar `ended`,
    // así que permitimos avanzar siempre. Para video directo (<video>),
    // requerimos que haya terminado al menos una vez.
    if (isDriveUrl(slide.content_url) || /youtube\.com|youtu\.be/i.test(slide.content_url || '')) {
      return true;
    }
    return !!videoEndedRef.current[idx];
  };

  const goToEval = () => navigate(`/academia/evaluacion/${courseId}`);

  const isInformative = course.has_evaluation === false;

  const handleFinishInformative = async () => {
    try {
      await api.updateCourseProgress(courseId, total - 1);
      toast.success('¡Curso completado! Este curso es informativo no tiene evaluación.');
      navigate('/academia');
    } catch (e) { toast.error('Error al finalizar el curso'); }
  };

  if (total === 0) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto p-4 sm:p-6 text-center">
          <BookOpen className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-bold mb-2">Este curso no tiene contenido</h2>
          <Button onClick={() => navigate('/academia')} variant="outline">Volver</Button>
        </div>
      </DashboardLayout>
    );
  }

  const renderSlide = () => {
    if (slide.slide_type === 'video') {
      const url = slide.content_url || '';
      const isDrive = isDriveUrl(url);
      const isYoutube = /youtube\.com|youtu\.be/i.test(url);
      if (isDrive || isYoutube) {
        return (
          <iframe
            src={toEmbedUrl(url)}
            title={`Slide ${idx + 1}`}
            className="w-full h-[50vh] sm:h-[60vh] border-0 bg-black"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
            data-testid="slide-video-iframe"
          />
        );
      }
      // Direct mp4/webm
      return (
        <video
          key={idx}
          controls
          playsInline
          src={url}
          onEnded={() => { videoEndedRef.current[idx] = true; }}
          className="w-full max-h-[50vh] sm:max-h-[60vh] bg-black"
          data-testid="slide-video"
        />
      );
    }
    if (slide.slide_type === 'image') {
      const isDrive = isDriveUrl(slide.content_url);
      return (
        <img
          src={isDrive ? toEmbedUrl(slide.content_url).replace('/preview', '/view') : slide.content_url}
          alt={`Slide ${idx + 1}`}
          className="w-full max-h-[50vh] sm:max-h-[60vh] object-contain bg-gray-100"
        />
      );
    }
    // presentation / pdf  -> iframe (también convertimos Drive a /preview)
    return (
      <iframe
        src={toEmbedUrl(slide.content_url)}
        title={`Slide ${idx + 1}`}
        className="w-full h-[50vh] sm:h-[60vh] border-0 bg-white"
      />
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-2 sm:px-0" data-testid="course-viewer-page">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/academia')} className="flex items-center gap-1 sm:gap-2">
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Volver</span>
          </Button>
          <span className="text-xs sm:text-sm text-gray-600">Slide {idx + 1} / {total}</span>
        </div>

        <h1 className="text-lg sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 px-1">{course.title}</h1>

        {isInformative && (
          <div className="mb-3 sm:mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2 text-sm text-blue-900" data-testid="informative-course-banner">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
            <span>Este curso es informativo no tiene evaluación. Se marcará como completado al terminar de ver todo el contenido.</span>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-3 sm:mb-4">
          {renderSlide()}
          {slide.audio_url && (
            <div className="p-2 sm:p-3 border-t border-gray-200 bg-gray-50">
              <audio controls src={slide.audio_url} className="w-full" />
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-3 sm:mb-4 overflow-hidden">
          <div className="bg-[#DC143C] h-2 transition-all" style={{ width: `${((idx + 1) / total) * 100}%` }} />
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between items-stretch sm:items-center pb-4">
          <Button variant="outline" onClick={handlePrev} disabled={idx === 0} className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <ArrowLeft className="h-4 w-4" /> Anterior
          </Button>

          {idx < total - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!canAdvance()}
              data-testid="next-slide-button"
              className="bg-[#DC143C] hover:bg-[#B91C1C] text-white flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <span className="text-sm sm:text-base">
                {slide.slide_type === 'video' && !canAdvance() ? 'Termina el video' : 'Siguiente'}
              </span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : isInformative ? (
            <Button
              onClick={handleFinishInformative}
              disabled={!allCompleted}
              data-testid="finish-informative-button"
              className="bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <CheckCircle className="h-4 w-4" /> Finalizar curso
            </Button>
          ) : (
            <Button
              onClick={goToEval}
              disabled={!allCompleted}
              data-testid="go-to-evaluation-button"
              className="bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <FileCheck className="h-4 w-4" /> Ir a Evaluación
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
